import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
} from 'lucide-react';
import {
  Button,
  Card,
  Description,
  Spinner,
  useOverlayState,
} from '@heroui/react';
import {
  associateApartmentMember,
  createApartment,
  createBlock,
  createParkingSpot,
  disassociateApartmentMember,
  getCondominiumDetails,
  listApartmentMembers,
  listApartmentsByBlock,
  updateApartment,
  updateBlock,
  updateParkingSpot,
  type Apartment,
  type ApartmentMember,
  type Block,
  type ParkingSpot,
} from '../../../api/condominiums';
import { listUsers, type UserWithMemberships } from '../../../api/users';
import { PageHeader } from '../../../components/page/page-header';
import { FormSwitch } from '../../../components/ui/form-switch';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { filterUsersForApartmentAssociation } from '../utils/apartment-user-filter';
import { ApartmentFormDrawer } from '../components/condominium-details/apartment-form-drawer';
import { AddBlockCard, BlockCard } from '../components/condominium-details/block-card';
import { BlockFormDrawer } from '../components/condominium-details/block-form-drawer';
import { SpotFormDrawer } from '../components/condominium-details/spot-form-drawer';
import {
  loadCondominiumFilters,
  saveCondominiumFilters,
} from '../utils/condominium-filters-storage';

function detailsQueryKey(id: string) {
  return ['admin', 'condominium', id, 'details'] as const;
}

export default function AdminCondominiumDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const blockDrawer = useOverlayState();
  const spotDrawer = useOverlayState();
  const apartmentDrawer = useOverlayState();

  const [filters, setFilters] = useState(loadCondominiumFilters);
  const [editingBlock, setEditingBlock] = useState<Block | null>(null);
  const [editingSpot, setEditingSpot] = useState<{ spot: ParkingSpot; blockId: string } | null>(
    null,
  );
  const [editingApartment, setEditingApartment] = useState<{
    apartment: Apartment;
    blockId: string;
  } | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [spotApartments, setSpotApartments] = useState<Apartment[]>([]);
  const [apartmentMembers, setApartmentMembers] = useState<ApartmentMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserWithMemberships[]>([]);

  useEffect(() => {
    saveCondominiumFilters(filters);
  }, [filters]);

  const { data: condominium, isLoading, isError, refetch } = useQuery({
    queryKey: id ? detailsQueryKey(id) : ['admin', 'condominium', 'none'],
    queryFn: () => getCondominiumDetails(id!),
    enabled: !!id,
  });

  const invalidate = useCallback(async () => {
    if (!id) return;
    await queryClient.invalidateQueries({ queryKey: detailsQueryKey(id) });
  }, [id, queryClient]);

  const loadUsersForApartment = useCallback(async (condominiumId: string) => {
    try {
      const users = await listUsers();
      setAvailableUsers(filterUsersForApartmentAssociation(users, condominiumId));
    } catch {
      setAvailableUsers([]);
    }
  }, []);

  const isSingleBlock =
    !!condominium &&
    condominium.blocks.length === 1 &&
    condominium.blocks[0]?.name === 'Bloco Único';
  const canEditDeleteBlocks = !isSingleBlock;

  const visibleBlocks = useMemo(() => {
    if (!condominium) return [];
    return condominium.blocks.filter((block) => {
      if (filters.showInactiveBlocks) return true;
      if (
        filters.showInactiveSpots &&
        block.parkingSpots.some((spot) => !spot.isActive)
      ) {
        return true;
      }
      return block.isActive;
    });
  }, [condominium, filters]);

  const blockMutation = useMutation({
    mutationFn: async (payload: { editing: Block | null; data: Parameters<typeof createBlock>[1] }) => {
      if (!id) throw new Error('Condomínio inválido');
      if (payload.editing) {
        return updateBlock(id, payload.editing.id, payload.data);
      }
      return createBlock(id, payload.data);
    },
    onSuccess: async () => {
      await invalidate();
      showSuccess('Bloco salvo com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const spotMutation = useMutation({
    mutationFn: async ({
      blockId,
      spotId,
      payload,
    }: {
      blockId: string;
      spotId?: string;
      payload: Parameters<typeof createParkingSpot>[1];
    }) => {
      if (spotId) return updateParkingSpot(blockId, spotId, payload);
      return createParkingSpot(blockId, payload);
    },
    onSuccess: async () => {
      await invalidate();
      showSuccess('Vaga salva com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const apartmentMutation = useMutation({
    mutationFn: async ({
      blockId,
      apartmentId,
      payload,
    }: {
      blockId: string;
      apartmentId?: string;
      payload: Parameters<typeof createApartment>[1];
    }) => {
      if (apartmentId) return updateApartment(blockId, apartmentId, payload);
      return createApartment(blockId, payload);
    },
    onSuccess: async () => {
      await invalidate();
      showSuccess('Apartamento salvo com sucesso.');
    },
    onError: (err) => showErrorFromUnknown(err),
  });

  const openCreateBlock = () => {
    setEditingBlock(null);
    blockDrawer.open();
  };

  const openEditBlock = (block: Block) => {
    setEditingBlock(block);
    blockDrawer.open();
  };

  const loadBlockApartments = useCallback(async (blockId: string): Promise<Apartment[]> => {
    try {
      return await listApartmentsByBlock(blockId);
    } catch {
      const fromDetails = condominium?.blocks.find((b) => b.id === blockId)?.apartments;
      return fromDetails ?? [];
    }
  }, [condominium?.blocks]);

  const openCreateSpot = async (blockId: string) => {
    setEditingSpot(null);
    setSelectedBlockId(blockId);
    setSpotApartments(await loadBlockApartments(blockId));
    spotDrawer.open();
  };

  const openEditSpot = async (spot: ParkingSpot, blockId: string) => {
    setEditingSpot({ spot, blockId });
    setSelectedBlockId(blockId);
    setSpotApartments(await loadBlockApartments(blockId));
    spotDrawer.open();
  };

  const openCreateApartment = async (blockId: string) => {
    setEditingApartment(null);
    setSelectedBlockId(blockId);
    setApartmentMembers([]);
    if (id) await loadUsersForApartment(id);
    apartmentDrawer.open();
  };

  const openEditApartment = async (apartment: Apartment, blockId: string) => {
    setEditingApartment({ apartment, blockId });
    setSelectedBlockId(blockId);
    if (id) await loadUsersForApartment(id);
    try {
      setApartmentMembers(await listApartmentMembers(blockId, apartment.id));
    } catch {
      setApartmentMembers([]);
    }
    apartmentDrawer.open();
  };

  const handleAssociateMember = async (
    blockId: string,
    apartmentId: string,
    userId: string,
  ) => {
    await associateApartmentMember(blockId, apartmentId, { userId });
    await invalidate();
    setApartmentMembers(await listApartmentMembers(blockId, apartmentId));
    showSuccess('Morador associado com sucesso.');
  };

  const handleDisassociateMember = async (
    blockId: string,
    apartmentId: string,
    userId: string,
  ) => {
    if (!window.confirm('Remover este morador do apartamento?')) return;
    await disassociateApartmentMember(blockId, apartmentId, userId);
    await invalidate();
    setApartmentMembers(await listApartmentMembers(blockId, apartmentId));
    showSuccess('Associação removida.');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex h-64 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (isError || !condominium || !id) {
    return (
      <div className="flex flex-col gap-6">
        <Card className="max-w-md">
          <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
            <AlertCircle className="text-danger" size={40} />
            <Description>Não foi possível carregar o condomínio.</Description>
            <div className="flex gap-2">
              <Button variant="ghost" onPress={() => navigate('/admin/condominiums')}>
                Voltar
              </Button>
              <Button variant="primary" onPress={() => void refetch()}>
                Tentar novamente
              </Button>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Button variant="ghost" className="w-fit" onPress={() => navigate('/admin/condominiums')}>
        <ArrowLeft size={18} />
        Voltar para condomínios
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader
          title={condominium.name}
          description={`Gerenciar blocos, apartamentos e vagas — @${condominium.slug}`}
        />
        <div className="flex shrink-0 flex-col gap-2 sm:items-end sm:pt-1">
          <FormSwitch
            size="sm"
            label="Blocos inativos"
            isSelected={filters.showInactiveBlocks}
            onChange={(checked) =>
              setFilters((prev) => ({ ...prev, showInactiveBlocks: checked }))
            }
          />
          <FormSwitch
            size="sm"
            label="Vagas inativas"
            isSelected={filters.showInactiveSpots}
            onChange={(checked) =>
              setFilters((prev) => ({ ...prev, showInactiveSpots: checked }))
            }
          />
        </div>
      </div>

      {condominium.blocks.length === 0 ? (
        <Card>
          <Card.Content className="flex flex-col items-center gap-4 py-12 text-center">
            <Building2 className="text-muted" size={48} />
            <Description>Nenhum bloco cadastrado.</Description>
            <Button variant="primary" onPress={openCreateBlock}>
              Adicionar bloco
            </Button>
          </Card.Content>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {visibleBlocks.map((block) => (
            <BlockCard
              key={block.id}
              block={block}
              isSingleBlock={isSingleBlock}
              canEditDeleteBlocks={canEditDeleteBlocks}
              showInactiveBlocks={filters.showInactiveBlocks}
              showInactiveSpots={filters.showInactiveSpots}
              onCreateApartment={(blockId) => void openCreateApartment(blockId)}
              onCreateSpot={(blockId) => void openCreateSpot(blockId)}
              onEditBlock={openEditBlock}
              onEditApartment={(apt, blockId) => void openEditApartment(apt, blockId)}
              onEditSpot={(spot, blockId) => void openEditSpot(spot, blockId)}
            />
          ))}
          <AddBlockCard onCreateBlock={openCreateBlock} />
        </div>
      )}

      <BlockFormDrawer
        state={blockDrawer}
        editing={editingBlock}
        onSubmit={async (payload) => {
          await blockMutation.mutateAsync({ editing: editingBlock, data: payload });
        }}
      />

      <SpotFormDrawer
        key={`${selectedBlockId ?? 'none'}-${editingSpot?.spot.id ?? 'new'}`}
        state={spotDrawer}
        editing={editingSpot}
        blockId={selectedBlockId}
        apartments={spotApartments}
        onSubmit={async (blockId, payload) => {
          await spotMutation.mutateAsync({
            blockId,
            spotId: editingSpot?.spot.id,
            payload,
          });
        }}
      />

      <ApartmentFormDrawer
        state={apartmentDrawer}
        blockId={selectedBlockId}
        condominiumName={condominium.name}
        editing={editingApartment}
        members={apartmentMembers}
        availableUsers={availableUsers}
        onSubmit={async (payload) => {
          if (!selectedBlockId) throw new Error('Bloco não selecionado');
          await apartmentMutation.mutateAsync({
            blockId: selectedBlockId,
            apartmentId: editingApartment?.apartment.id,
            payload,
          });
        }}
        onAssociateMember={handleAssociateMember}
        onDisassociateMember={handleDisassociateMember}
      />
    </div>
  );
}
